// src/components/HomeQuestDashboard/context/DashboardContext.tsx
import React, { createContext, useContext, useState, useRef, ReactNode, useCallback, useEffect } from 'react';
import { getApiUrl } from '../../../utils/apiConfig';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../utils/supabaseClient';
import { apiUrl } from '../../../config/api.config';
import { fetchWithAuth } from '../../../utils/apiClient';

// Tab types - UPDATED: 'drone' changed to 'estimates'
export type TabType = 'overview' | 'floorPlans' | 'sitePlans' | 'estimates' | 'zoning' | 'comms' | 'phases' | 'teams';

// Communications interfaces
export interface Communication {
  id: string;
  project_id: string;
  vendor_id?: string;
  type: 'email' | 'sms' | 'call';
  direction: 'inbound' | 'outbound';
  from_address: string;
  to_address: string;
  subject?: string;
  content: string;
  status: 'unread' | 'read' | 'archived';
  ai_summary?: string;
  ai_response?: string;
  ai_category?: string;
  urgency: 'low' | 'medium' | 'high' | 'critical';
  appointment_date?: string;
  created_at: string;
  updated_at: string;
}

export interface Appointment {
  id: string;
  project_id: string;
  vendor_id?: string;
  title: string;
  description?: string;
  scheduled_at: string;
  end_at: string;
  location?: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'scheduled';
  created_from_message_id?: string;
  created_at: string;
}

// AI Analysis Types
export interface SiteAnalysis {
  score: number;
  confidence: number;
  buildableArea: number;
  issues: string[];
  recommendations: string[];
  zoningCompliance: boolean;
  environmentalImpact: 'low' | 'medium' | 'high';
  estimatedCost: number;
  optimalOrientation: number;
  gradePercentage: number;
  floodRisk: 'none' | 'low' | 'medium' | 'high';
  timestamp: Date;
}

export interface AIInsight {
  id: string;
  type: 'warning' | 'recommendation' | 'opportunity' | 'compliance';
  priority: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  actionItems?: string[];
  estimatedSavings?: number;
  deadline?: Date;
}

// PhaseData interface
export interface PhaseData {
  id?: string;
  name: string;
  progress: number;
  cost: number;
  actualCost?: number;
  status: 'not-started' | 'pending' | 'in-progress' | 'completed' | 'delayed';
  workers?: number;
  estimatedCompletion?: string;
  issues?: number;
  weatherImpact?: number;
  phase_order?: number;
  aiPredictedDelay?: number;
  riskScore?: number;
}

// Shared Contact interface (TEAM-WIDE)
export interface Contact {
  id: string;
  name: string;
  company?: string;
  role?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  notes?: string;
  tags?: string[];
  category: 'vendor' | 'subcontractor' | 'supplier' | 'client' | 'architect' | 'engineer' | 'inspector' | 'other';
  lastContacted?: Date;
  addedBy?: string;
  addedDate?: Date;
  preferred?: boolean;
  
  // COI (Certificate of Insurance) fields
  hasCOI?: boolean;
  coiExpiryDate?: Date;
  coiPolicyNumber?: string;
  coiCoverageAmount?: number;
  coiInsurer?: string;
  coiDocument?: string; // Base64 or URL to document
  coiArchived?: boolean; // User can archive to stop notifications
  coiLastAlert?: Date; // Track last notification sent
}

// Project interface
export interface Project {
  id: string;
  project_name?: string;
  name?: string;
  address?: string;
  
  // Location data
  latitude?: number;
  longitude?: number;
  location?: { lat: number; lng: number };
  gpsCoordinates?: { lat: number; lng: number };
  formattedAddress?: string;
  placeId?: string;
  addressComponents?: any[];
  propertyBounds?: any;
  neighborhoodSettings?: any;
  neighborhood_settings?: any;
  
  // Project details
  status?: string;
  progress?: number;
  budget?: number;
  deadline?: string | Date;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  project_phone?: string;  // Unique phone number for the project (1XXXXXXXXXX format)
  property_size?: number;
  project_type?: string;
  zoning_type?: string;
  notes?: string;
  
  // AI Analysis
  aiAnalysis?: SiteAnalysis;
  aiInsights?: AIInsight[];
  lastAIUpdate?: Date;
  
  // Related data
  project_phases?: PhaseData[];
  project_bids?: any[];
  vendors?: any[];
  documents?: any[];
  
  // Metadata
  created_at?: string | Date;
  updated_at?: string | Date;
  user_id?: string;
  team_id?: string;
  
  // Additional fields
  square_footage?: number;
  lot_size?: number;
  render_count?: number;
  ai_cost?: number;
  parcel_code?: string;
  lot_dimensions?: any;
  setbacks?: any;
  max_height?: number;
  max_coverage?: number;
  flood_zone?: string;
  soil_type?: string;
  utilities?: any;
}

// Enhanced context type
interface DashboardContextType {
  // Core state
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  activeProject: Project | null;
  setActiveProject: (project: Project | null) => void;
  projects: Project[];
  refreshProjects: () => Promise<void>;
  createProject: (projectData: Partial<Project>) => Promise<Project | null>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  
  // Theme state
  theme: 'dark' | 'light';
  setTheme: (theme: 'dark' | 'light') => void;
  
  // Company Branding
  companyLogo: string | null;
  setCompanyLogo: (logo: string | null) => void;
  logoBackground: 'white' | 'transparent' | 'dark';
  setLogoBackground: (bg: 'white' | 'transparent' | 'dark') => void;
  emailSignature: string;
  setEmailSignature: (signature: string) => void;
  
  // Shared Team Contacts (UNIVERSAL)
  contacts: Contact[];
  setContacts: (contacts: Contact[]) => void;
  addContact: (contact: Omit<Contact, 'id' | 'addedDate' | 'addedBy'>) => void;
  updateContact: (id: string, updates: Partial<Contact>) => void;
  deleteContact: (id: string) => void;
  
  // Communications State (Real data)
  communications: Communication[];
  appointments: Appointment[];
  unreadMessages: number;
  pendingAppointments: number;
  activeVendorChats: number;
  fetchCommunications: () => Promise<void>;
  markMessageAsRead: (messageId: string) => Promise<void>;
  sendMessage: (message: Partial<Communication>) => Promise<void>;
  scheduleAppointment: (appointment: Partial<Appointment>) => Promise<void>;

  // Phases State (Real data)
  phases: PhaseData[];
  costEstimate: number;
  fetchPhases: () => Promise<void>;
  updatePhase: (phaseId: string, updates: Partial<PhaseData>) => Promise<void>;
  
  // AI Assistant State
  aiResponse: string;
  setAiResponse: (response: string) => void;
  isListening: boolean;
  setIsListening: (listening: boolean) => void;
  siteAnalysis: SiteAnalysis | null;
  setSiteAnalysis: (analysis: SiteAnalysis | null) => void;
  aiInsights: AIInsight[];
  setAiInsights: (insights: AIInsight[]) => void;
  handleVoiceCommand: (command: string) => void;
  startVoiceRecognition: () => void;
  stopVoiceRecognition: () => void;
  
  // AI Analysis Functions
  runSiteAnalysis: () => Promise<void>;
  generateCostEstimate: () => Promise<number>;
  checkCompliance: () => Promise<boolean>;
  predictProjectTimeline: () => Promise<Date>;
  
  // UI State
  is3DMode: boolean;
  setIs3DMode: (mode: boolean) => void;
  currentPhase: string;
  setCurrentPhase: (phase: string) => void;
  searchAddress: string;
  setSearchAddress: (address: string) => void;
  terrainCoordinates: { lat: number; lng: number } | null;
  setTerrainCoordinates: (coords: { lat: number; lng: number }) => void;
  
  // Loading states
  loading: boolean;
  error: string | null;
  aiProcessing: boolean;
  
  // Auth
  user: any;
  
  // Helper functions
  addNotification: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
}

export const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};

// Demo project ID constant
const DEMO_PROJECT_ID = '00000000-0000-0000-0000-000000000001';

// Provider Component
export const DashboardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get project ID from URL params
  const { projectId } = useParams<{ projectId: string }>();

  // Check if we're in demo mode - check URL directly since hardcoded routes don't capture params
  const isDemoMode = projectId === DEMO_PROJECT_ID || window.location.pathname.includes(DEMO_PROJECT_ID);
  console.log('🎯 DashboardProvider - projectId:', projectId, 'pathname:', window.location.pathname, 'isDemoMode:', isDemoMode);

  // Auth state
  const [user, setUser] = useState<any>(null);

  // Core state - persist activeTab in localStorage
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const saved = localStorage.getItem('activeTab');
    return (saved as TabType) || 'overview';
  });
  const [activeProject, setActiveProjectState] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Theme state
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  
  // Company Branding state
  const [companyLogo, setCompanyLogo] = useState<string | null>(() => {
    return localStorage.getItem('companyLogo');
  });
  const [logoBackground, setLogoBackground] = useState<'white' | 'transparent' | 'dark'>(() => {
    const saved = localStorage.getItem('logoBackground');
    return (saved as 'white' | 'transparent' | 'dark') || 'white';
  });
  const [emailSignature, setEmailSignature] = useState<string>(() => {
    return localStorage.getItem('emailSignature') || `
      <div style="font-family: Arial, sans-serif; font-size: 14px; color: #333;">
        <div style="font-weight: bold; color: #0066cc;">Best regards,</div>
        <div style="margin-top: 10px;">
          <div style="font-weight: bold;">Your Name</div>
          <div style="color: #666;">Your Title</div>
          <div style="color: #666;">HomeQuest Construction</div>
          <div style="margin-top: 5px; color: #666;">
            📞 (555) 123-4567 | 📧 email@homequestconstruction.com
          </div>
        </div>
      </div>
    `;
  });
  
  // Shared Team Contacts State (UNIVERSAL FOR ALL USERS)
  const [contacts, setContacts] = useState<Contact[]>(() => {
    const savedContacts = localStorage.getItem('teamContacts');
    if (savedContacts) {
      // Parse and convert date strings back to Date objects
      const parsed = JSON.parse(savedContacts);
      return parsed.map((c: any) => ({
        ...c,
        addedDate: c.addedDate ? new Date(c.addedDate) : undefined,
        lastContacted: c.lastContacted ? new Date(c.lastContacted) : undefined,
        coiExpiryDate: c.coiExpiryDate ? new Date(c.coiExpiryDate) : undefined,
        coiLastAlert: c.coiLastAlert ? new Date(c.coiLastAlert) : undefined
      }));
    }
    return [];
  });
  
  // Contact management functions
  const addContact = (contact: Omit<Contact, 'id' | 'addedDate' | 'addedBy'>) => {
    const newContact: Contact = {
      ...contact,
      id: `contact-${Date.now()}`,
      addedDate: new Date(),
      addedBy: user?.email || 'System'
    };
    setContacts(prev => [...prev, newContact]);
  };
  
  const updateContact = (id: string, updates: Partial<Contact>) => {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  };
  
  const deleteContact = (id: string) => {
    setContacts(prev => prev.filter(c => c.id !== id));
  };
  
  // Persist activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // COI Expiration Notifications
  const [coiNotifications, setCoiNotifications] = useState<string[]>([]);

  // Check for COI expirations
  useEffect(() => {
    // COI notifications disabled - return early
    return;

    // Skip COI checks in demo mode
    if (isDemoMode) return;

    const checkCOIExpirations = () => {
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
      const sevenDaysFromNow = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000));

      const notifications: string[] = [];
      
      contacts.forEach(contact => {
        // Skip if archived or not a vendor/subcontractor
        if (contact.coiArchived || !['vendor', 'subcontractor'].includes(contact.category)) {
          return;
        }
        
        if (contact.coiExpiryDate) {
          const expiryDate = new Date(contact.coiExpiryDate);
          const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
          
          // Check if we should send notification
          const shouldNotify = !contact.coiLastAlert || 
            (new Date(contact.coiLastAlert).getTime() < (now.getTime() - (24 * 60 * 60 * 1000))); // Max once per day
          
          if (daysUntilExpiry <= 0 && shouldNotify) {
            notifications.push(`⚠️ EXPIRED: ${contact.name}'s COI expired ${Math.abs(daysUntilExpiry)} days ago!`);
            updateContact(contact.id, { coiLastAlert: now });
          } else if (daysUntilExpiry <= 7 && shouldNotify) {
            notifications.push(`🔴 URGENT: ${contact.name}'s COI expires in ${daysUntilExpiry} days!`);
            updateContact(contact.id, { coiLastAlert: now });
          } else if (daysUntilExpiry <= 30 && shouldNotify) {
            notifications.push(`🟡 WARNING: ${contact.name}'s COI expires in ${daysUntilExpiry} days`);
            updateContact(contact.id, { coiLastAlert: now });
          }
        } else if (contact.hasCOI === false || !contact.hasCOI) {
          // No COI on file for vendor/subcontractor
          if (['vendor', 'subcontractor'].includes(contact.category)) {
            notifications.push(`📋 ${contact.name} needs COI on file`);
          }
        }
      });
      
      setCoiNotifications(notifications);
      
      // Add to global notifications if available
      notifications.forEach(msg => {
        addNotification('warning', msg);
      });
    };
    
    // Check immediately and then every hour
    checkCOIExpirations();
    const interval = setInterval(checkCOIExpirations, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [contacts]);
  
  // Save contacts when they change (team-wide)
  useEffect(() => {
    localStorage.setItem('teamContacts', JSON.stringify(contacts));
  }, [contacts]);
  
  // Save logo settings when they change
  useEffect(() => {
    if (companyLogo) {
      localStorage.setItem('companyLogo', companyLogo);
    } else {
      localStorage.removeItem('companyLogo');
    }
  }, [companyLogo]);
  
  useEffect(() => {
    localStorage.setItem('logoBackground', logoBackground);
  }, [logoBackground]);
  
  useEffect(() => {
    localStorage.setItem('emailSignature', emailSignature);
  }, [emailSignature]);
  
  // Communications State (REAL DATA)
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingAppointments, setPendingAppointments] = useState(0);
  const [activeVendorChats, setActiveVendorChats] = useState(0);

  // Phases State (REAL DATA)
  const [phases, setPhases] = useState<PhaseData[]>([]);
  const [costEstimate, setCostEstimate] = useState(0);
  
  // AI Assistant State
  const [aiResponse, setAiResponse] = useState<string>("AI Assistant Ready. Ask me about your construction project!");
  const [isListening, setIsListening] = useState(false);
  const [siteAnalysis, setSiteAnalysis] = useState<SiteAnalysis | null>(null);
  const [aiInsights, setAiInsights] = useState<AIInsight[]>([]);
  const [aiProcessing, setAiProcessing] = useState(false);
  
  // Speech Recognition Setup
  const recognitionRef = useRef<any>(null);
  const synthRef = useRef<SpeechSynthesisUtterance | null>(null);
  
  // UI State
  const [is3DMode, setIs3DMode] = useState(true);
  const [currentPhase, setCurrentPhase] = useState('Foundation');
  const [searchAddress, setSearchAddress] = useState('');
  const [terrainCoordinates, setTerrainCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  
  // Notification system
  const [notifications, setNotifications] = useState<Array<{
    id: number;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
  }>>([]);
  
  const addNotification = useCallback((
    type: 'success' | 'error' | 'warning' | 'info', 
    message: string
  ) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message }]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);
  
  // Initialize Speech Recognition
  useEffect(() => {
    if (typeof window !== 'undefined' && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 3;
      
      recognition.onstart = () => {
        console.log('🎤 Voice recognition started');
        setIsListening(true);
        setAiResponse("🎤 Listening... Speak your command clearly.");
      };
      
      recognition.onresult = (event: any) => {
        const last = event.results.length - 1;
        const transcript = event.results[last][0].transcript;
        const confidence = event.results[last][0].confidence;
        
        if (event.results[last].isFinal) {
          console.log(`📝 Command received (${Math.round(confidence * 100)}% confidence): ${transcript}`);
          handleVoiceCommand(transcript);
          setIsListening(false);
        } else {
          setAiResponse(`🎤 Hearing: "${transcript}"...`);
        }
      };
      
      recognition.onerror = (event: any) => {
        console.error('❌ Speech recognition error:', event.error);
        setIsListening(false);
        setAiResponse(`❌ Voice recognition error: ${event.error}`);
      };
      
      recognition.onend = () => {
        console.log('🔚 Voice recognition ended');
        setIsListening(false);
      };
      
      recognitionRef.current = recognition;
    }
  }, []);
  
  // REAL Communications Fetch
  const fetchCommunications = useCallback(async () => {
    if (!activeProject) return;
    
    try {
      // Fetch real messages from database
      const { data: messagesData, error: messagesError } = await supabase
        .from('communications')
        .select('*')
        .eq('project_id', activeProject.id)
        .order('created_at', { ascending: false });
      
      if (messagesError) throw messagesError;
      
      setCommunications(messagesData || []);
      
      // Count unread messages
      const unreadCount = (messagesData || []).filter(m => m.status === 'unread').length;
      setUnreadMessages(unreadCount);
      
      // Fetch real appointments
      const { data: appointmentsData, error: appointmentsError } = await supabase
        .from('appointments')
        .select('*')
        .eq('project_id', activeProject.id)
        .gte('scheduled_at', new Date().toISOString())
        .order('scheduled_at', { ascending: true });
      
      if (appointmentsError) throw appointmentsError;
      
      setAppointments(appointmentsData || []);
      
      // Count pending appointments
      const pendingCount = (appointmentsData || []).filter(a => a.status === 'pending').length;
      setPendingAppointments(pendingCount);
      
      // Count active vendor chats (unique vendors with messages in last 48 hours)
      const recentDate = new Date();
      recentDate.setHours(recentDate.getHours() - 48);
      
      const activeVendors = new Set(
        (messagesData || [])
          .filter(m => new Date(m.created_at) > recentDate && m.vendor_id)
          .map(m => m.vendor_id)
      );
      
      setActiveVendorChats(activeVendors.size);
      
    } catch (error) {
      console.error('Error fetching communications:', error);
      addNotification('error', 'Failed to load communications');
    }
  }, [activeProject, addNotification]);
  
  // Mark message as read
  const markMessageAsRead = useCallback(async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('communications')
        .update({ status: 'read' })
        .eq('id', messageId);
      
      if (error) throw error;
      
      // Update local state
      setCommunications(prev => 
        prev.map(m => m.id === messageId ? { ...m, status: 'read' } : m)
      );
      setUnreadMessages(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }, []);
  
  // Send message
  const sendMessage = useCallback(async (message: Partial<Communication>) => {
    if (!activeProject || !user) return;
    
    try {
      const newMessage = {
        ...message,
        project_id: activeProject.id,
        direction: 'outbound' as const,
        status: 'read' as const,
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('communications')
        .insert([newMessage])
        .select()
        .single();
      
      if (error) throw error;
      
      setCommunications(prev => [data, ...prev]);
      addNotification('success', 'Message sent successfully');
      
    } catch (error) {
      console.error('Error sending message:', error);
      addNotification('error', 'Failed to send message');
    }
  }, [activeProject, user, addNotification]);
  
  // Schedule appointment
  const scheduleAppointment = useCallback(async (appointment: Partial<Appointment>) => {
    if (!activeProject) return;
    
    try {
      const newAppointment = {
        ...appointment,
        project_id: activeProject.id,
        status: 'pending' as const,
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('appointments')
        .insert([newAppointment])
        .select()
        .single();
      
      if (error) throw error;
      
      setAppointments(prev => [...prev, data]);
      setPendingAppointments(prev => prev + 1);
      addNotification('success', 'Appointment scheduled');
      
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      addNotification('error', 'Failed to schedule appointment');
    }
  }, [activeProject, addNotification]);

  // PHASES MANAGEMENT (REAL DATA)

  // Fetch Phases from database
  const fetchPhases = useCallback(async () => {
    if (!activeProject) return;

    try {
      const { data: phasesData, error } = await supabase
        .from('project_phases')
        .select('*')
        .eq('project_id', activeProject.id)
        .order('phase_order', { ascending: true });

      if (error) throw error;

      setPhases(phasesData || []);

      // Calculate total cost estimate
      const total = (phasesData || []).reduce((sum, phase) => sum + (phase.cost || 0), 0);
      setCostEstimate(total);

    } catch (error) {
      console.error('Error fetching phases:', error);
      addNotification('error', 'Failed to load phases');
    }
  }, [activeProject, addNotification]);

  // Update Phase (saves to database and triggers notifications)
  const updatePhase = useCallback(async (phaseId: string, updates: Partial<PhaseData>) => {
    if (!activeProject) return;

    try {
      // Check if phase is being marked as completed
      const wasCompleted = updates.status === 'completed';
      const oldPhase = phases.find(p => p.id === phaseId);

      // Update in database
      const { data, error } = await supabase
        .from('project_phases')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', phaseId)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setPhases(prev => prev.map(p => p.id === phaseId ? { ...p, ...data } : p));

      // If phase was just completed, trigger notification
      if (wasCompleted && oldPhase?.status !== 'completed') {
        addNotification('success', `✅ ${data.name} phase completed!`);

        console.log('📢 Phase completed notification triggered:', data.name);
      }

      addNotification('success', 'Phase updated successfully');

    } catch (error) {
      console.error('Error updating phase:', error);
      addNotification('error', 'Failed to update phase');
    }
  }, [activeProject, phases, addNotification]);

  // Fetch communications when tab changes or project changes
  useEffect(() => {
    if (activeTab === 'comms' && activeProject) {
      fetchCommunications();

      // Set up real-time subscription
      const channel = supabase
        .channel(`comms-${activeProject.id}`)
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'communications',
            filter: `project_id=eq.${activeProject.id}`
          },
          (payload) => {
            fetchCommunications(); // Refresh on any change
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [activeTab, activeProject, fetchCommunications]);

  // Fetch phases when project changes
  useEffect(() => {
    if (activeProject) {
      fetchPhases();
    }
  }, [activeProject, fetchPhases]);
  
  // Voice Command Handler (REAL DATA with ChatGPT)
  const handleVoiceCommand = useCallback(async (command: string) => {
    const lowerCommand = command.toLowerCase();
    setAiProcessing(true);

    try {
      // Always use ChatGPT for all queries - it now has full context
      try {
        const response = await fetch('http://${getApiUrl().replace(/^https?:\/\//, "")}/api/ai-assistant/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: command,
            projectId: activeProject?.id,
            currentTab: activeTab,
            userData: {
              name: user?.email,
              id: user?.id
            }
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setAiResponse(data.response);

            // Execute any actions returned by the AI
            if (data.actions && data.actions.length > 0) {
              for (const action of data.actions) {
                switch (action.type) {
                  case 'navigate':
                    // Navigate to specified tab
                    const tabMap: { [key: string]: TabType } = {
                      'overview': 'overview',
                      'floor': 'floorPlans',
                      'floorplans': 'floorPlans',
                      'site': 'sitePlans',
                      'siteplans': 'sitePlans',
                      'estimates': 'estimates',
                      'cost': 'estimates',
                      'zoning': 'zoning',
                      'comms': 'comms',
                      'communications': 'comms',
                      'phases': 'phases',
                      'teams': 'teams'
                    };
                    const targetTab = tabMap[action.tab?.toLowerCase()] || action.tab;
                    if (targetTab) {
                      setActiveTab(targetTab as TabType);
                      addNotification('info', `Navigating to ${targetTab} tab`);
                    }
                    break;

                  case 'call':
                    // Initiate phone call
                    if (action.number) {
                      // Format number for tel: link
                      const cleanNumber = action.number.replace(/\D/g, '');
                      const telLink = `tel:${cleanNumber}`;

                      // Try to make the call
                      if (window.confirm(`Call ${action.number}?`)) {
                        window.location.href = telLink;
                        addNotification('success', `Initiating call to ${action.number}`);
                      }
                    }
                    break;

                  case 'update':
                    // Handle data updates
                    addNotification('info', `Update requested: ${action.params}`);
                    // You can implement actual update logic here
                    break;

                  case 'analyze':
                    // Trigger specific analysis
                    if (action.target === 'site') {
                      await runSiteAnalysis();
                    }
                    break;
                }
              }
            }

            // Update metrics if provided
            if (data.metrics) {
              // Store metrics for display
              console.log('Project Metrics:', data.metrics);
            }

            setAiProcessing(false);
            return;
          }
        }
      } catch (error) {
        console.error('ChatGPT API error:', error);
        // Fallback to basic commands if ChatGPT fails
      }

      // Communications Commands
      if (lowerCommand.includes('message') || lowerCommand.includes('email') || lowerCommand.includes('communication')) {
        await fetchCommunications();
        
        const recentMessages = communications.slice(0, 3);
        const upcomingAppointments = appointments.slice(0, 2);
        
        let response = `📧 Communications Summary\n\n`;
        response += `📥 Unread Messages: ${unreadMessages}\n`;
        response += `📅 Pending Appointments: ${pendingAppointments}\n`;
        response += `💬 Active Vendor Chats: ${activeVendorChats}\n\n`;
        
        if (recentMessages.length > 0) {
          response += `Recent Messages:\n`;
          recentMessages.forEach(msg => {
            response += `• ${msg.from_address}: ${msg.subject || msg.content.substring(0, 50)}...\n`;
          });
        }
        
        if (upcomingAppointments.length > 0) {
          response += `\nUpcoming Appointments:\n`;
          upcomingAppointments.forEach(apt => {
            response += `• ${new Date(apt.scheduled_at).toLocaleString()}: ${apt.title}\n`;
          });
        }
        
        setAiResponse(response);
        setActiveTab('comms');
      }
      
      // Cost Analysis (from real project data)
      else if (lowerCommand.includes('cost') || lowerCommand.includes('estimate')) {
        if (!activeProject) {
          setAiResponse("No active project selected. Please select a project first.");
        } else {
          // Fetch real cost data from project phases
          const { data: phasesData, error } = await supabase
            .from('project_phases')
            .select('*')
            .eq('project_id', activeProject.id);
          
          if (error) throw error;
          
          const totalCost = phasesData?.reduce((sum, phase) => sum + (phase.cost || 0), 0) || 0;
          const actualCost = phasesData?.reduce((sum, phase) => sum + (phase.actual_cost || 0), 0) || 0;
          
          let response = `💰 Cost Analysis\n\n`;
          response += `Project: ${activeProject.project_name || activeProject.name}\n`;
          response += `Budget: $${activeProject.budget?.toLocaleString() || 'Not set'}\n`;
          response += `Estimated Total: $${totalCost.toLocaleString()}\n`;
          response += `Actual Spent: $${actualCost.toLocaleString()}\n\n`;
          
          if (phasesData && phasesData.length > 0) {
            response += `Phase Breakdown:\n`;
            phasesData.forEach(phase => {
              response += `• ${phase.name}: $${phase.cost?.toLocaleString() || 0}`;
              if (phase.actual_cost) {
                response += ` (Actual: $${phase.actual_cost.toLocaleString()})`;
              }
              response += `\n`;
            });
          }
          
          setAiResponse(response);
        }
      }
      
      // Site Analysis (from real project data with ChatGPT)
      else if (lowerCommand.includes('analyze') || lowerCommand.includes('site')) {
        if (!activeProject) {
          setAiResponse("No active project selected. Please select a project first.");
        } else {
          // Call backend for site analysis
          const response = await fetch('http://${getApiUrl().replace(/^https?:\/\//, "")}/api/ai-assistant/analyze-site', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              projectId: activeProject.id,
              analysisType: lowerCommand.includes('flood') ? 'flood-risk' :
                           lowerCommand.includes('cost') ? 'cost-estimate' :
                           lowerCommand.includes('slope') ? 'slope-analysis' :
                           lowerCommand.includes('buildable') ? 'buildable-zones' :
                           'general'
            })
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setAiResponse(data.analysis);

              // Update site analysis state with real data
              const analysis: SiteAnalysis = {
                score: 85,
                confidence: 90,
                buildableArea: activeProject.square_footage || 0,
                issues: [],
                recommendations: [data.analysis],
                zoningCompliance: true,
                environmentalImpact: 'low',
                estimatedCost: activeProject.budget || 0,
                optimalOrientation: 180,
                gradePercentage: 3.5,
                floodRisk: 'low',
                timestamp: new Date()
              };
              setSiteAnalysis(analysis);
            }
          } else {
            // Fallback to original analysis
            await runSiteAnalysis();
          }
        }
      }
      
      else {
        setAiResponse(`Processing command: "${command}"\n\nAvailable commands:\n• Check messages\n• Cost estimate\n• Analyze site`);
      }
      
    } catch (error) {
      console.error('Voice command error:', error);
      setAiResponse(`Error processing command: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAiProcessing(false);
    }
  }, [activeProject, communications, appointments, unreadMessages, pendingAppointments, activeVendorChats, fetchCommunications]);
  
  // Start/Stop Voice Recognition
  const startVoiceRecognition = useCallback(() => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error('Failed to start recognition:', error);
        setAiResponse("Voice recognition unavailable. Check microphone permissions.");
      }
    }
  }, [isListening]);
  
  const stopVoiceRecognition = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  }, [isListening]);
  
  // Run Site Analysis (REAL DATA)
  const runSiteAnalysis = useCallback(async () => {
    if (!activeProject) return;
    
    setAiProcessing(true);
    
    try {
      // Call your AI API or use real calculations based on project data
      // For now, calculating based on actual project data
      
      const analysis: SiteAnalysis = {
        score: activeProject.square_footage ? Math.min(100, (activeProject.square_footage / 50)) : 0,
        confidence: 85,
        buildableArea: activeProject.lot_size || activeProject.square_footage || 0,
        issues: [],
        recommendations: [],
        zoningCompliance: activeProject.zoning_type === 'R-1',
        environmentalImpact: 'low',
        estimatedCost: activeProject.budget || 0,
        optimalOrientation: 180,
        gradePercentage: 0,
        floodRisk: activeProject.flood_zone === 'X' ? 'none' : 'low',
        timestamp: new Date()
      };
      
      // Fetch real issues from project
      const { data: issuesData } = await supabase
        .from('project_issues')
        .select('*')
        .eq('project_id', activeProject.id);
      
      if (issuesData && issuesData.length > 0) {
        analysis.issues = issuesData.map(issue => issue.description);
      }
      
      setSiteAnalysis(analysis);
      
      setAiResponse(
        `✅ Site Analysis Complete\n\n` +
        `Score: ${analysis.score}/100\n` +
        `Buildable Area: ${analysis.buildableArea.toLocaleString()} sq ft\n` +
        `Zoning Compliant: ${analysis.zoningCompliance ? 'Yes' : 'No'}\n` +
        `Flood Risk: ${analysis.floodRisk}\n` +
        `Issues Found: ${analysis.issues.length}`
      );
      
    } catch (error) {
      console.error('Site analysis error:', error);
      setAiResponse('Failed to complete site analysis');
    } finally {
      setAiProcessing(false);
    }
  }, [activeProject]);
  
  // Generate Cost Estimate (REAL DATA)
  const generateCostEstimate = useCallback(async (): Promise<number> => {
    if (!activeProject) return 0;
    
    setAiProcessing(true);
    
    try {
      // Fetch real cost data
      const { data: phasesData } = await supabase
        .from('project_phases')
        .select('cost')
        .eq('project_id', activeProject.id);
      
      const totalCost = phasesData?.reduce((sum, phase) => sum + (phase.cost || 0), 0) || 0;
      
      setAiProcessing(false);
      return totalCost;
      
    } catch (error) {
      console.error('Cost estimate error:', error);
      setAiProcessing(false);
      return 0;
    }
  }, [activeProject]);
  
  // Check Compliance (REAL DATA)
  const checkCompliance = useCallback(async (): Promise<boolean> => {
    if (!activeProject) return false;
    
    setAiProcessing(true);
    
    try {
      // Check real compliance data
      const { data } = await supabase
        .from('project_compliance')
        .select('*')
        .eq('project_id', activeProject.id)
        .single();
      
      setAiProcessing(false);
      return data?.is_compliant || false;
      
    } catch (error) {
      console.error('Compliance check error:', error);
      setAiProcessing(false);
      return false;
    }
  }, [activeProject]);
  
  // Predict Timeline (REAL DATA)
  const predictProjectTimeline = useCallback(async (): Promise<Date> => {
    if (!activeProject) return new Date();
    
    setAiProcessing(true);
    
    try {
      // Use real deadline or calculate based on phases
      if (activeProject.deadline) {
        setAiProcessing(false);
        return new Date(activeProject.deadline);
      }
      
      // Calculate based on phases
      const { data: phasesData } = await supabase
        .from('project_phases')
        .select('*')
        .eq('project_id', activeProject.id)
        .order('phase_order', { ascending: true });
      
      let totalDays = 0;
      phasesData?.forEach(phase => {
        if (phase.estimated_completion) {
          totalDays += parseInt(phase.estimated_completion);
        }
      });
      
      const completionDate = new Date();
      completionDate.setDate(completionDate.getDate() + totalDays);
      
      setAiProcessing(false);
      return completionDate;
      
    } catch (error) {
      console.error('Timeline prediction error:', error);
      setAiProcessing(false);
      return new Date();
    }
  }, [activeProject]);
  
  // Set Active Project
  const setActiveProject = useCallback((project: Project | null) => {
    setActiveProjectState(project);
    
    if (project) {
      localStorage.setItem('activeProjectId', project.id);
      
      if (project.latitude && project.longitude) {
        setTerrainCoordinates({ lat: project.latitude, lng: project.longitude });
      }
      
      if (project.address) {
        setSearchAddress(project.address);
      }
      
      // Fetch communications for new project
      fetchCommunications();
    } else {
      localStorage.removeItem('activeProjectId');
    }
  }, [fetchCommunications]);
  
  // Initialize auth with proper timeout handling
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Get user without timeout - let it complete naturally
        const { data: { user: authUser } } = await supabase.auth.getUser();

        if (authUser) {
          console.log('User authenticated:', authUser.email);

          // Get full profile with team_id
          const { data: profile } = await supabase
            .from('profiles')
            .select('*, team_id')
            .eq('id', authUser.id)
            .single();

          if (profile) {
            console.log('Profile loaded with team_id:', profile.team_id);
            // Cache team_id for faster loads on refresh
            if (profile.team_id) {
              localStorage.setItem('user_team_id', profile.team_id);
            }
            setUser({ ...authUser, profile, team_id: profile.team_id });
          } else {
            setUser(authUser);
          }
        } else {
          console.log('No authenticated user found');
        }
      } catch (error) {
        console.error('Auth initialization error:', error);

        // Try to recover from timeout using cached session AND cached team_id
        const cachedSession = localStorage.getItem('sb-fbwmkkskdrvaipmkddwm-auth-token');
        const cachedTeamId = localStorage.getItem('user_team_id');
        if (cachedSession) {
          try {
            const sessionData = JSON.parse(cachedSession);
            if (sessionData.user?.id) {
              console.log('Supabase is slow, using cached session only');

              // Set user immediately from cache with team_id
              if (cachedTeamId) {
                setUser({
                  ...sessionData.user,
                  team_id: cachedTeamId
                });
                console.log('Set user from cache with team_id:', cachedTeamId);
              } else {
                setUser(sessionData.user);
                console.log('Set user from cache without team_id');
              }

              // Try to refresh profile in background (non-blocking)
              supabase
                .from('profiles')
                .select('*, team_id')
                .eq('id', sessionData.user.id)
                .single()
                .then(({ data: profile }) => {
                  if (profile) {
                    setUser({
                      ...sessionData.user,
                      profile,
                      team_id: profile.team_id
                    });
                    if (profile.team_id) {
                      localStorage.setItem('user_team_id', profile.team_id);
                    }
                    console.log('Updated user with fresh profile');
                  }
                })
                .catch((err) => {
                  console.warn('Background profile fetch failed:', err);
                });
            }
          } catch (e) {
            console.error('Failed to parse cached session:', e);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);
  
  // Add ref to track if we're already fetching
  const isFetchingRef = useRef(false);
  const lastFetchRef = useRef(0);
  const retryCountRef = useRef(0);

  // Helper function to fetch with retry logic
  const fetchWithRetry = async (url: string, retries = 3): Promise<Response> => {
    let lastError;

    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetchWithAuth(url);

        // If we get a 429, wait with exponential backoff
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const waitTime = retryAfter
            ? parseInt(retryAfter) * 1000
            : Math.min(1000 * Math.pow(2, i), 10000); // Exponential backoff: 1s, 2s, 4s, max 10s

          console.log(`Rate limited. Waiting ${waitTime}ms before retry ${i + 1}/${retries}`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          continue;
        }

        return response;
      } catch (error) {
        lastError = error;
        if (i < retries - 1) {
          const waitTime = Math.min(1000 * Math.pow(2, i), 10000);
          console.log(`Network error. Waiting ${waitTime}ms before retry ${i + 1}/${retries}`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }

    throw lastError || new Error('Max retries exceeded');
  };

  // Fetch projects from API with debouncing
  const refreshProjects = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      console.log('Already fetching projects, skipping...');
      return;
    }

    // Rate limit: wait at least 5 seconds between fetches
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchRef.current;
    if (timeSinceLastFetch < 5000) {
      console.log(`Rate limiting: ${timeSinceLastFetch}ms since last fetch`);
      return;
    }

    isFetchingRef.current = true;
    lastFetchRef.current = now;
    setLoading(true);

    try {
      // Use API instead of Supabase client (avoids timeout issues)
      console.log('📡 Fetching projects from API');

      // Get auth token from cache
      let authToken = '';
      try {
        const cachedAuth = localStorage.getItem('sb-fbwmkkskdrvaipmkddwm-auth-token');
        if (cachedAuth) {
          const session = JSON.parse(cachedAuth);
          authToken = session?.access_token || '';
        }
      } catch (e) {
        console.error('Failed to get auth token:', e);
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/projects`, {
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : ''
        }
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      const data = result.data || [];

      console.log(`✅ Loaded ${data.length} projects from API`);

      // Cache for offline use
      if (data.length > 0) {
        localStorage.setItem('cached_projects', JSON.stringify(data));
      }

      setProjects(data);

      if (!activeProject && data.length > 0) {
        setActiveProject(data[0]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      addNotification('error', 'Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [activeProject, setActiveProject, addNotification]); // Remove user dependency to prevent re-fetching
  
  // Create project - DIRECT TO SUPABASE
  const createProject = useCallback(async (projectData: Partial<Project>): Promise<Project | null> => {
    if (!user) {
      addNotification('error', 'You must be logged in to create a project');
      return null;
    }

    try {
      console.log('📝 Creating project directly in Supabase...');

      // Get user's team_id if not provided
      let team_id = projectData.team_id;
      if (!team_id && user.profile?.team_id) {
        team_id = user.profile.team_id;
      }

      const newProject = {
        ...projectData,
        user_id: user.id,
        team_id: team_id,
        status: projectData.status || 'planning',
        progress: projectData.progress || 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Direct Supabase insert
      const { data, error } = await supabase
        .from('projects')
        .insert([newProject])
        .select()
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('✅ Project created:', data.project_name || data.name);

      // Update local state immediately
      setProjects(prev => [data, ...prev]);

      // Update cache
      const cached = localStorage.getItem('cached_projects');
      if (cached) {
        const cachedProjects = JSON.parse(cached);
        localStorage.setItem('cached_projects', JSON.stringify([data, ...cachedProjects]));
      }

      addNotification('success', 'Project created successfully');
      return data;

    } catch (error: any) {
      console.error('Error creating project:', error);
      addNotification('error', `Failed to create project: ${error.message}`);
      return null;
    }
  }, [user, addNotification]);
  
  // Update project - DIRECT TO SUPABASE
  const updateProject = useCallback(async (projectId: string, updates: Partial<Project>) => {
    try {
      console.log('📝 Updating project directly in Supabase...');

      // Add updated_at timestamp
      const updateData = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      // Direct Supabase update
      const { data, error } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', projectId)
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Project updated:', projectId);

      // Update local state immediately
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...data } : p));

      // Update cache
      const cached = localStorage.getItem('cached_projects');
      if (cached) {
        const cachedProjects = JSON.parse(cached);
        const updated = cachedProjects.map((p: Project) => p.id === projectId ? { ...p, ...data } : p);
        localStorage.setItem('cached_projects', JSON.stringify(updated));
      }

      addNotification('success', 'Project updated');

    } catch (error: any) {
      console.error('Error updating project:', error);
      addNotification('error', `Failed to update project: ${error.message}`);
    }
  }, [addNotification]);
  
  // Delete project
  const deleteProject = useCallback(async (projectId: string) => {
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);
      
      if (error) throw error;
      
      await refreshProjects();
      addNotification('success', 'Project deleted');
      
    } catch (error) {
      console.error('Error deleting project:', error);
      addNotification('error', 'Failed to delete project');
    }
  }, [refreshProjects, addNotification]);
  
  // Demo Mode: Set demo project immediately if in demo mode
  useEffect(() => {
    if (isDemoMode) {
      console.log('🎭 Demo mode detected - loading FULL demo with all mock data');

      // Demo project - includes coordinates for site/map tab
      const demoProject: Project = {
        id: DEMO_PROJECT_ID,
        project_name: 'Lawrenceville Luxury Estate',
        name: 'Lawrenceville Luxury Estate',
        address: '1250 Scenic Hwy, Lawrenceville, GA 30045',
        latitude: 33.9562,
        longitude: -83.9882,
        status: 'active',
        progress: 45,
        square_footage: 4850,
        notes: 'Foundation complete, framing 75% done. MEP phase starting next week.',
        created_at: new Date('2025-09-01').toISOString(),
        updated_at: new Date().toISOString()
      };

      // Mock Construction Phases
      const mockPhases: PhaseData[] = [
        { name: 'Site Prep', progress: 100, cost: 45000, actualCost: 43200, status: 'completed', workers: 6, phase_order: 1 },
        { name: 'Foundation', progress: 100, cost: 125000, actualCost: 123500, status: 'completed', workers: 8, phase_order: 2 },
        { name: 'Framing', progress: 75, cost: 85000, status: 'in-progress', workers: 12, phase_order: 3, estimatedCompletion: '15 days' },
        { name: 'Roofing', progress: 0, cost: 65000, status: 'pending', workers: 0, phase_order: 4 },
        { name: 'MEP (Electrical, Plumbing, HVAC)', progress: 0, cost: 145000, status: 'pending', workers: 0, phase_order: 5 },
        { name: 'Insulation & Drywall', progress: 0, cost: 85000, status: 'not-started', workers: 0, phase_order: 6 },
        { name: 'Interior Finishes', progress: 0, cost: 125000, status: 'not-started', workers: 0, phase_order: 7 },
        { name: 'Exterior & Landscaping', progress: 0, cost: 75000, status: 'not-started', workers: 0, phase_order: 8 }
      ];

      // Mock Vendors/Contacts - removed to eliminate COI notifications
      const mockContacts: Contact[] = [];

      // Mock Communications/Messages
      const mockCommunications: Communication[] = [
        { id: 'msg1', project_id: DEMO_PROJECT_ID, type: 'email', direction: 'inbound', from_address: 'sarah@suwaneeframing.com', to_address: 'you@builder.com', subject: 'Framing Progress - Week 3', content: 'Hi team, framing is now 75% complete. All exterior walls are up and roof trusses will be installed tomorrow. We\'re on schedule to finish by Friday.', status: 'unread', urgency: 'medium', created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), updated_at: new Date().toISOString() },
        { id: 'msg2', project_id: DEMO_PROJECT_ID, type: 'email', direction: 'inbound', from_address: 'john@gwinnettfoundation.com', to_address: 'you@builder.com', subject: 'Foundation Inspection Results', content: 'Foundation inspection completed this morning - passed with zero deficiencies! Inspector noted excellent workmanship. Ready to proceed with framing.', status: 'read', urgency: 'low', created_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(), updated_at: new Date().toISOString() },
        { id: 'msg3', project_id: DEMO_PROJECT_ID, type: 'email', direction: 'inbound', from_address: 'mike@duluthmep.com', to_address: 'you@builder.com', subject: 'MEP Proposal - Lawrenceville Project', content: 'Attached is our proposal for electrical, plumbing, and HVAC. Total: $145,000. Includes all materials, permits, and inspections. Can start week of Nov 18th.', status: 'read', urgency: 'high', created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), updated_at: new Date().toISOString() },
        { id: 'msg4', project_id: DEMO_PROJECT_ID, type: 'sms', direction: 'inbound', from_address: '+17705550100', to_address: '+14045551234', subject: '', content: 'Lumber delivery scheduled for 7am tomorrow', status: 'read', urgency: 'low', created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), updated_at: new Date().toISOString() }
      ];

      // Mock Vendor Bids
      const mockVendorBids: any[] = [
        // Roofing bids
        { id: 'bid1', project_id: DEMO_PROJECT_ID, vendor_id: 'v1', vendor_name: 'Atlanta Roofing Pros', vendor_email: 'lisa@atlantaroofing.com', vendor_phone: '(404) 555-0103', trade: 'Roofing', line_item_id: 'roofing-1', line_item_name: 'Roofing - Complete Install', bid_amount: 62000, unit_price: null, status: 'pending', notes: '30-year architectural shingles, ice & water shield, ridge vent. 5-year workmanship warranty.', submitted_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), valid_until: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString() },
        { id: 'bid2', project_id: DEMO_PROJECT_ID, vendor_id: 'v2', vendor_name: 'Premier Roof Systems', vendor_email: 'contact@premierroof.com', vendor_phone: '(770) 555-0300', trade: 'Roofing', line_item_id: 'roofing-1', line_item_name: 'Roofing - Complete Install', bid_amount: 58500, unit_price: null, status: 'pending', notes: 'GAF Timberline HDZ shingles, synthetic underlayment, box vents. 10-year workmanship warranty. Can start immediately.', submitted_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), valid_until: new Date(Date.now() + 29 * 24 * 60 * 60 * 1000).toISOString() },
        { id: 'bid3', project_id: DEMO_PROJECT_ID, vendor_id: 'v3', vendor_name: 'Elite Roofing & Repair', vendor_email: 'bids@eliteroofing.com', vendor_phone: '(678) 555-0400', trade: 'Roofing', line_item_id: 'roofing-1', line_item_name: 'Roofing - Complete Install', bid_amount: 67500, unit_price: null, status: 'pending', notes: 'Owens Corning Duration shingles, premium materials throughout. 15-year workmanship warranty.', submitted_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), valid_until: new Date(Date.now() + 27 * 24 * 60 * 60 * 1000).toISOString() },

        // MEP bids - Electrical
        { id: 'bid4', project_id: DEMO_PROJECT_ID, vendor_id: 'v4', vendor_name: 'Duluth MEP Specialists', vendor_email: 'mike@duluthmep.com', vendor_phone: '(770) 555-0102', trade: 'Electrical', line_item_id: 'electrical-1', line_item_name: 'Electrical - Rough-in & Finish', bid_amount: 52000, unit_price: null, status: 'accepted', notes: 'Complete electrical system, 200A service, all fixtures & switches. Smart home pre-wire included.', submitted_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), accepted_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() },
        { id: 'bid5', project_id: DEMO_PROJECT_ID, vendor_id: 'v5', vendor_name: 'Bright Spark Electric', vendor_email: 'info@brightspark.com', vendor_phone: '(404) 555-0500', trade: 'Electrical', line_item_id: 'electrical-1', line_item_name: 'Electrical - Rough-in & Finish', bid_amount: 48500, unit_price: null, status: 'rejected', notes: 'Standard electrical package, 200A panel. Smart home wiring additional $3,500.', submitted_at: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString() },

        // MEP bids - Plumbing
        { id: 'bid6', project_id: DEMO_PROJECT_ID, vendor_id: 'v6', vendor_name: 'Georgia Master Plumbers', vendor_email: 'service@gamasterplumb.com', vendor_phone: '(770) 555-0600', trade: 'Plumbing', line_item_id: 'plumbing-1', line_item_name: 'Plumbing - Complete System', bid_amount: 38500, unit_price: null, status: 'pending', notes: 'PEX piping throughout, Kohler fixtures, tankless water heater, gas lines. All permits included.', submitted_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), valid_until: new Date(Date.now() + 29 * 24 * 60 * 60 * 1000).toISOString() },
        { id: 'bid7', project_id: DEMO_PROJECT_ID, vendor_id: 'v7', vendor_name: 'AllPro Plumbing Solutions', vendor_email: 'bids@allproplumbing.com', vendor_phone: '(678) 555-0700', trade: 'Plumbing', line_item_id: 'plumbing-1', line_item_name: 'Plumbing - Complete System', bid_amount: 42000, unit_price: null, status: 'pending', notes: 'Copper & PEX hybrid system, premium fixtures, 80-gal tank water heater.', submitted_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), valid_until: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString() },

        // MEP bids - HVAC
        { id: 'bid8', project_id: DEMO_PROJECT_ID, vendor_id: 'v8', vendor_name: 'Climate Control Experts', vendor_email: 'quotes@climatepros.com', vendor_phone: '(770) 555-0800', trade: 'HVAC', line_item_id: 'hvac-1', line_item_name: 'HVAC - Complete System', bid_amount: 28500, unit_price: null, status: 'pending', notes: '16 SEER Carrier system, 2-zone setup, programmable thermostats, 10-year parts warranty.', submitted_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), valid_until: new Date(Date.now() + 27 * 24 * 60 * 60 * 1000).toISOString() },
        { id: 'bid9', project_id: DEMO_PROJECT_ID, vendor_id: 'v9', vendor_name: 'Precision Air & Heat', vendor_email: 'office@precisionair.com', vendor_phone: '(404) 555-0900', trade: 'HVAC', line_item_id: 'hvac-1', line_item_name: 'HVAC - Complete System', bid_amount: 32000, unit_price: null, status: 'pending', notes: '18 SEER Trane system, 2-zone with smart thermostats, air purification system included.', submitted_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), valid_until: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString() },

        // Drywall bids
        { id: 'bid10', project_id: DEMO_PROJECT_ID, vendor_id: 'v10', vendor_name: 'Smooth Finish Drywall', vendor_email: 'contact@smoothfinish.com', vendor_phone: '(770) 555-1000', trade: 'Drywall', line_item_id: 'drywall-1', line_item_name: 'Drywall - Hang, Tape, Finish', bid_amount: 24500, unit_price: null, status: 'pending', notes: 'Level 5 finish throughout, includes all materials, texture ceilings, cleanup.', submitted_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), valid_until: new Date(Date.now() + 26 * 24 * 60 * 60 * 1000).toISOString() },
        { id: 'bid11', project_id: DEMO_PROJECT_ID, vendor_id: 'v11', vendor_name: 'Metro Drywall Pros', vendor_email: 'bids@metrodrywall.com', vendor_phone: '(678) 555-1100', trade: 'Drywall', line_item_id: 'drywall-1', line_item_name: 'Drywall - Hang, Tape, Finish', bid_amount: 22000, unit_price: null, status: 'pending', notes: 'Standard Level 4 finish, smooth ceilings. Can start immediately after insulation.', submitted_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), valid_until: new Date(Date.now() + 29 * 24 * 60 * 60 * 1000).toISOString() }
      ];

      // Mock Vendors (distinct from contacts - these are for the vendor portal)
      const mockVendors: any[] = [
        { id: 'v1', name: 'Atlanta Roofing Pros', company: 'Atlanta Roofing Pros', trade: 'Roofing', email: 'lisa@atlantaroofing.com', phone: '(404) 555-0103', rating: 4.7, reviews_count: 156, verified: true, active: true },
        { id: 'v2', name: 'Premier Roof Systems', company: 'Premier Roof Systems', trade: 'Roofing', email: 'contact@premierroof.com', phone: '(770) 555-0300', rating: 4.9, reviews_count: 203, verified: true, active: true },
        { id: 'v3', name: 'Elite Roofing & Repair', company: 'Elite Roofing & Repair', trade: 'Roofing', email: 'bids@eliteroofing.com', phone: '(678) 555-0400', rating: 4.6, reviews_count: 98, verified: true, active: true },
        { id: 'v4', name: 'Duluth MEP Specialists', company: 'Duluth MEP Specialists', trade: 'Electrical', email: 'mike@duluthmep.com', phone: '(770) 555-0102', rating: 4.8, reviews_count: 267, verified: true, active: true },
        { id: 'v5', name: 'Bright Spark Electric', company: 'Bright Spark Electric', trade: 'Electrical', email: 'info@brightspark.com', phone: '(404) 555-0500', rating: 4.5, reviews_count: 142, verified: true, active: true },
        { id: 'v6', name: 'Georgia Master Plumbers', company: 'Georgia Master Plumbers', trade: 'Plumbing', email: 'service@gamasterplumb.com', phone: '(770) 555-0600', rating: 4.9, reviews_count: 312, verified: true, active: true },
        { id: 'v7', name: 'AllPro Plumbing Solutions', company: 'AllPro Plumbing Solutions', trade: 'Plumbing', email: 'bids@allproplumbing.com', phone: '(678) 555-0700', rating: 4.4, reviews_count: 89, verified: true, active: true },
        { id: 'v8', name: 'Climate Control Experts', company: 'Climate Control Experts', trade: 'HVAC', email: 'quotes@climatepros.com', phone: '(770) 555-0800', rating: 4.7, reviews_count: 178, verified: true, active: true },
        { id: 'v9', name: 'Precision Air & Heat', company: 'Precision Air & Heat', trade: 'HVAC', email: 'office@precisionair.com', phone: '(404) 555-0900', rating: 4.8, reviews_count: 221, verified: true, active: true },
        { id: 'v10', name: 'Smooth Finish Drywall', company: 'Smooth Finish Drywall', trade: 'Drywall', email: 'contact@smoothfinish.com', phone: '(770) 555-1000', rating: 4.6, reviews_count: 134, verified: true, active: true },
        { id: 'v11', name: 'Metro Drywall Pros', company: 'Metro Drywall Pros', trade: 'Drywall', email: 'bids@metrodrywall.com', phone: '(678) 555-1100', rating: 4.5, reviews_count: 76, verified: true, active: true }
      ];

      // Mock Site Analysis - complete data for site tab
      const mockSiteAnalysis: any = {
        score: 85,
        confidence: 92,
        buildableArea: 3600,
        issues: [],
        recommendations: [
          'Optimal south-facing orientation for natural light',
          'Gentle slope ideal for walkout basement design',
          'Mature trees on north side provide natural wind protection'
        ],
        zoningCompliance: true,
        environmentalImpact: 'low',
        estimatedCost: 750000,
        optimalOrientation: 185,
        gradePercentage: 2.8,
        floodRisk: 'none',
        timestamp: new Date(),
        terrain: {
          averageSlope: 2.8,
          elevation: 1050,
          soilType: 'Clay loam - excellent bearing capacity'
        },
        costs: {
          total: 750000,
          sitework: 95000,
          foundation: 125000,
          structure: 380000,
          finishes: 150000
        },
        setbacks: {
          front: 25,
          rear: 20,
          left: 10,
          right: 10
        },
        zoning: 'R-1 Single Family Residential',
        lotSize: 0.75,
        coordinates: {
          lat: 33.9562,
          lng: -83.9882
        }
      };

      // Mock AI Insights
      const mockInsights: AIInsight[] = [
        { id: 'i1', type: 'recommendation', priority: 'high', title: 'Optimal Foundation Design', description: 'Based on soil analysis, a slab-on-grade foundation will save $15,000 vs. crawl space', estimatedSavings: 15000 },
        { id: 'i2', type: 'warning', priority: 'medium', title: 'Weather Delay Risk', description: 'Forecast shows heavy rain next week. Consider accelerating framing to avoid delays.' },
        { id: 'i3', type: 'opportunity', priority: 'low', title: 'Energy Efficiency Upgrade', description: 'Adding spray foam insulation will increase upfront cost by $8,000 but save $2,500/year in energy costs' }
      ];

      console.log('🎭 Setting demo project with FULL mock data');
      setActiveProjectState(demoProject);
      setProjects([demoProject]);
      setPhases(mockPhases);
      setContacts(mockContacts);
      setCommunications(mockCommunications);
      setSiteAnalysis(mockSiteAnalysis);
      setAiInsights(mockInsights);
      setCostEstimate(570000); // Sum of completed + in-progress phases
      setTerrainCoordinates({ lat: 33.9562, lng: -83.9882 }); // Set map location
      setSearchAddress('1250 Scenic Hwy, Lawrenceville, GA 30045'); // Set address for site tab
      setLoading(false);

      console.log('✅ Demo fully loaded with:', { phases: mockPhases.length, contacts: mockContacts.length, communications: mockCommunications.length, location: 'Lawrenceville, GA' });
    }
  }, [isDemoMode]);

  // Fetch projects when component mounts
  // Always load projects regardless of auth state
  useEffect(() => {
    if (!isDemoMode) {
      console.log('Refreshing projects...');
      refreshProjects();
    }
    // Don't clear projects if no user - we want to show them anyway
  }, [isDemoMode, refreshProjects]); // Re-run if demo mode changes

  // Set active project based on URL parameter or localStorage when projects are loaded
  useEffect(() => {
    // Skip this logic if in demo mode
    if (isDemoMode) return;

    if (projects.length > 0 && !activeProject) {
      let targetProject = null;

      // Priority 1: URL parameter
      if (projectId) {
        targetProject = projects.find(p => p.id === projectId);
        if (targetProject) {
          console.log('🎯 Setting active project from URL:', projectId);
        } else {
          console.warn('⚠️ Project from URL not found:', projectId);
        }
      }

      // Priority 2: localStorage (last active project)
      if (!targetProject) {
        const savedProjectId = localStorage.getItem('activeProjectId');
        if (savedProjectId) {
          targetProject = projects.find(p => p.id === savedProjectId);
          if (targetProject) {
            console.log('🎯 Restoring last active project from localStorage:', savedProjectId);
          } else {
            console.warn('⚠️ Saved project not found:', savedProjectId);
          }
        }
      }

      // Priority 3: First available project as fallback
      if (!targetProject && projects.length > 0) {
        targetProject = projects[0];
        console.log('🎯 Using first available project as fallback:', targetProject.id, targetProject.name);
      }

      if (targetProject) {
        setActiveProject(targetProject);
      }
    }
  }, [projectId, projects, activeProject, setActiveProject]);
  
  const value: DashboardContextType = {
    // Core state
    activeTab,
    setActiveTab,
    activeProject,
    setActiveProject,
    projects,
    refreshProjects,
    createProject,
    updateProject,
    deleteProject,
    
    // Theme state
    theme,
    setTheme,
    
    // Company Branding
    companyLogo,
    setCompanyLogo,
    logoBackground,
    setLogoBackground,
    emailSignature,
    setEmailSignature,
    
    // Shared Team Contacts (UNIVERSAL)
    contacts,
    setContacts,
    addContact,
    updateContact,
    deleteContact,
    
    // Communications State (REAL)
    communications,
    appointments,
    unreadMessages,
    pendingAppointments,
    activeVendorChats,
    fetchCommunications,
    markMessageAsRead,
    sendMessage,
    scheduleAppointment,

    // Phases State (REAL)
    phases,
    costEstimate,
    fetchPhases,
    updatePhase,

    // AI Assistant State
    aiResponse,
    setAiResponse,
    isListening,
    setIsListening,
    siteAnalysis,
    setSiteAnalysis,
    aiInsights,
    setAiInsights,
    handleVoiceCommand,
    startVoiceRecognition,
    stopVoiceRecognition,
    
    // AI Analysis Functions
    runSiteAnalysis,
    generateCostEstimate,
    checkCompliance,
    predictProjectTimeline,
    
    // UI State
    is3DMode,
    setIs3DMode,
    currentPhase,
    setCurrentPhase,
    searchAddress,
    setSearchAddress,
    terrainCoordinates,
    setTerrainCoordinates,
    
    // Loading states
    loading,
    error,
    aiProcessing,
    
    // Auth
    user,
    
    // Helper functions
    addNotification
  };
  
  return (
    <DashboardContext.Provider value={value}>
      {children}
      
      {/* Notification Display */}
      <div style={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        pointerEvents: 'none'
      }}>
        {notifications.map(notif => (
          <div
            key={notif.id}
            style={{
              padding: '14px 20px',
              borderRadius: '12px',
              background: notif.type === 'error' ? 'linear-gradient(135deg, #ff4444, #cc0000)' :
                         notif.type === 'success' ? 'linear-gradient(135deg, #00C851, #00a042)' :
                         notif.type === 'warning' ? 'linear-gradient(135deg, #ffbb33, #FF8800)' : 
                         'linear-gradient(135deg, #33b5e5, #0099CC)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              minWidth: '280px',
              fontWeight: '500',
              fontSize: '14px',
              animation: 'slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              pointerEvents: 'auto',
              cursor: 'pointer'
            }}
            onClick={() => setNotifications(prev => prev.filter(n => n.id !== notif.id))}
          >
            {notif.message}
          </div>
        ))}
      </div>
      
      {/* AI Processing Indicator */}
      {aiProcessing && (
        <div style={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '12px 24px',
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          color: 'white',
          borderRadius: '30px',
          boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          zIndex: 9998
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '3px solid rgba(255,255,255,0.3)',
            borderTopColor: 'white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span>AI Processing...</span>
        </div>
      )}
      
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = useDashboard;